/**
 * Shared document <title> rules for SEO (Bing/Google ~60–70 char guidance).
 */

import { formatAgencySeoLocationPhrase } from '../agency/agencySeoLocationPhrase.js';
import { resolveEventPriceDisplay } from './trainingCost.js';

export const SEO_TITLE_BRAND_FULL = " | American Police Training";
export const SEO_TITLE_BRAND_SHORT = " | APT";

const DEFAULT_BRAND_LABEL = "american police training";

export function titleIncludesSeoBrand(
  title: string,
  brandLabel: string = DEFAULT_BRAND_LABEL,
): boolean {
  return title.toLowerCase().includes(brandLabel);
}

export function appendSeoBrandIfNeeded(
  title: string,
  brand: string = SEO_TITLE_BRAND_FULL,
): string {
  const t = title.replace(/\s+/g, " ").trim();
  if (!t) return brand.trim();
  return titleIncludesSeoBrand(t) ? t : `${t}${brand}`;
}

/** Default max length for generic titles */
const DEFAULT_MAX = 60;

/** Listing / hub `<title>` — Semrush “too long” is typically >70. */
export const BRANDED_SEO_TITLE_MAX_CHARS = 70;

/** Event detail `<title>` — room for city + date without dropping brand. */
export const EVENT_DETAIL_TITLE_MAX_CHARS = 70;

/** Agency profile `<title>` — room for geo + ORI + slug ref without mangling to “… Law | APT” */
export const AGENCY_PROFILE_TITLE_MAX_CHARS = 65;

const AGENCY_ROLE_SUFFIX = " - Law Enforcement Agency";

const US_STATE_AT_HINT_END = /,\s*[A-Z]{2}\s*$/;

/**
 * True when the location hint ends with a US-style 2-letter state code after a comma
 * (e.g. "Bell County, Killeen, TX"). Used to decide whether a slug # is still needed for uniqueness.
 */
export function locationHintHasUsStateSuffix(hint: string): boolean {
  const t = hint.replace(/\s+/g, " ").trim();
  if (!t) return false;
  return US_STATE_AT_HINT_END.test(t);
}

/**
 * Shorten a "County, City, ST" style hint without dropping the trailing state code first.
 */
export function truncateLocationHintPreservingState(
  hint: string,
  maxChars: number,
): string {
  const t = hint.replace(/\s+/g, " ").trim();
  if (!t || t.length <= maxChars) return t;
  const m = t.match(/^(.*)(,\s*[A-Z]{2}\s*)$/);
  if (m?.[1] != null && m[2] != null) {
    const head = m[1].trim();
    const statePart = m[2];
    const budget = maxChars - statePart.length;
    if (budget >= 1) {
      let headTrunc = truncateSeoTitle(head, budget);
      let out = `${headTrunc}${statePart}`.replace(/\s+/g, " ").trim();
      while (out.length > maxChars && headTrunc.length > 1) {
        headTrunc = truncateSeoTitle(head, headTrunc.length - 1);
        out = `${headTrunc}${statePart}`.replace(/\s+/g, " ").trim();
      }
      if (out.length <= maxChars) return out;
    }
    const st = statePart.replace(/^\s*,\s*/, "").trim();
    if (st.length <= maxChars) return st;
  }
  return truncateSeoTitle(t, maxChars);
}

/**
 * Trim to maxChars, preferring a word boundary (space) in the second half of the slice.
 */
export function truncateSeoTitle(text: string, maxChars: number = DEFAULT_MAX): string {
  const t = text.replace(/\s+/g, " ").trim();
  if (!t || t.length <= maxChars) return t;
  const cut = t.slice(0, maxChars);
  const minWordBreak = Math.max(8, Math.floor(maxChars * 0.45));
  const lastSpace = cut.lastIndexOf(" ");
  const end =
    lastSpace >= minWordBreak ? lastSpace : Math.min(maxChars, t.length);
  return t.slice(0, end).trim();
}

/** Document `<title>` budget shared by SSR and the client (Bing ~70, one character of slack). */
export const FINAL_SEO_TITLE_MAX_CHARS = 69;

const BROKEN_BRAND_ELLIPSIS = /American Police(?:\.{3}|…)/gi;
const TRAILING_BROKEN_AMERICAN = /\s*[|\-–—]\s*American\s*$/i;
const TRAILING_BRAND_PATTERNS = [
  /\s*\|\s*American Police Training\s*$/i,
  /\s*[-–—]\s*American Police Training\s*$/i,
  /\s*\|\s*APT\s*$/i,
];

function hasIntactSeoBrand(title: string): boolean {
  return /american police training/i.test(title) || /\|\s*APT\s*$/i.test(title);
}

function stripTrailingSeoBrand(title: string): string {
  let out = title;
  for (const pattern of TRAILING_BRAND_PATTERNS) {
    out = out.replace(pattern, "");
  }
  return out.replace(/\s+/g, " ").trim();
}

function packFinalSeoTitle(core: string, maxChars: number): string {
  let c = core.replace(/\s+/g, " ").trim().replace(/[|\-–—]\s*$/g, "").trim();
  if (!c) c = "Page";
  if (hasIntactSeoBrand(c)) {
    if (c.length <= maxChars) return c;
    const stripped = stripTrailingSeoBrand(c);
    if (stripped !== c) return packFinalSeoTitle(stripped, maxChars);
    return truncateSeoTitle(c, maxChars);
  }
  const full = `${c}${SEO_TITLE_BRAND_FULL}`;
  if (full.length <= maxChars) return full;
  const short = `${c}${SEO_TITLE_BRAND_SHORT}`;
  if (short.length <= maxChars) return short;
  const budget = Math.max(1, maxChars - SEO_TITLE_BRAND_SHORT.length);
  return `${truncateSeoTitle(c, budget)}${SEO_TITLE_BRAND_SHORT}`;
}

export type FinalizeSeoTitleOptions = {
  /** Bing-oriented cap. Defaults to 69. */
  max?: number;
  /**
   * Curated registry titles are already final. Do not add a brand or shorten them.
   */
  verbatim?: boolean;
};

/**
 * One title rule for SSR auto copy and the client fallback.
 * Keeps a single brand, never cuts inside it, and never emits "..." or "…".
 */
export function finalizeSeoTitle(
  raw: string,
  options?: FinalizeSeoTitleOptions,
): string {
  const max = options?.max ?? FINAL_SEO_TITLE_MAX_CHARS;
  const normalized = String(raw ?? "").replace(/\s+/g, " ").trim();
  if (options?.verbatim) return normalized;
  if (!normalized) return "American Police Training";
  const stripped = normalized
    .replace(BROKEN_BRAND_ELLIPSIS, "")
    .replace(TRAILING_BROKEN_AMERICAN, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[|\-–—]\s*$/g, "")
    .trim();
  return packFinalSeoTitle(stripped || "Page", max);
}

const AGENCY_META_BRAND_TAIL = " on American Police Training";
const AGENCY_META_DESCRIPTION_MAX = 160;

/** Keep name, type, and location, and drop the brand tail when it would exceed 160. */
function packAgencyProfileDescription(sentence: string, refTail: string): string {
  const core = sentence.replace(/\s+/g, " ").trim().replace(/\.+$/g, "");
  const ref = refTail.replace(/\s+/g, " ").trim();
  const withBrand = ref
    ? `${core}${AGENCY_META_BRAND_TAIL}. ${ref}`
    : `${core}${AGENCY_META_BRAND_TAIL}.`;
  if (withBrand.length <= AGENCY_META_DESCRIPTION_MAX) return withBrand;
  return ref ? `${core}. ${ref}` : `${core}.`;
}

/** Core + brand, preferring full brand then `| APT`, never dropping the suffix. */
export function packBrandedSeoTitle(
  core: string,
  maxChars: number = BRANDED_SEO_TITLE_MAX_CHARS,
): string {
  const c = core.replace(/\s+/g, " ").trim() || "Page";
  for (const brand of [SEO_TITLE_BRAND_FULL, SEO_TITLE_BRAND_SHORT]) {
    const out = `${c}${brand}`;
    if (out.length <= maxChars) return out;
  }
  const suffix = SEO_TITLE_BRAND_SHORT;
  const hard = truncateSeoTitle(c, Math.max(8, maxChars - suffix.length));
  return `${hard}${suffix}`;
}

/**
 * Expand abbreviated agency names in `<title>` only (H1 stays the stored name).
 * Lifts “X PD” above Semrush’s short-title floor without changing the profile H1.
 */
export function expandAgencyNameForSeoTitle(name: string): string {
  return name
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\bPDs\b/g, "Police Departments")
    .replace(/\bPD\b/g, "Police Department")
    .replace(/\bSOs\b/g, "Sheriff's Offices")
    .replace(/\bSO\b/g, "Sheriff's Office");
}

function minimalLocationForAgencyTitle(loc: string): string {
  const t = loc.replace(/\s+/g, " ").trim();
  if (!t) return "";
  const m = t.match(/,\s*([A-Z]{2})\s*$/);
  if (m?.[1]) return m[1];
  return truncateLocationHintPreservingState(t, 12);
}

export type AgencyProfileMetaDescriptionInput = {
  name: string;
  agencyTypeLabel: string;
  city?: string | null;
  state?: string | null;
  countyName?: string | null;
  isOperational: boolean;
  closurePublicNotes?: string | null;
  /** URL slug — used for unique Ref tail when geo/ORI are thin */
  slug?: string | null;
};

/**
 * Many agency slugs are `{numericId}-{kebab-name}`; exposes stable digits for title/meta uniqueness.
 */
export function extractAgencySlugNumericPrefix(
  slug?: string | null,
): string | undefined {
  const s = (slug ?? "").trim();
  if (!s) return undefined;
  const m = /^(\d{4,})-/.exec(s);
  return m ? m[1] : undefined;
}

/**
 * Meta description for public agency profiles — keep in sync with SSR
 * (`seoHtmlInjection`) and `AgencyDetailPage` `SEOHead`.
 */
export function buildAgencyProfileMetaDescription(
  input: AgencyProfileMetaDescriptionInput,
): string {
  const name = input.name.trim() || "Agency";
  const agencyTypeLabel =
    input.agencyTypeLabel.trim() || "Law Enforcement Agency";
  const ref = extractAgencySlugNumericPrefix(input.slug);
  const refTail = ref ? ` Ref ${ref}.` : "";
  const locPhrase = formatAgencySeoLocationPhrase({
    city: input.city,
    state: input.state,
    countyName: input.countyName,
    agencyTypeLabel: input.agencyTypeLabel,
    agencyName: input.name,
  });
  if (!input.isOperational && input.closurePublicNotes?.trim()) {
    return packAgencyProfileDescription(
      `${input.closurePublicNotes.trim()} — ${agencyTypeLabel} (${name}${locPhrase ? `, ${locPhrase}` : ""})`,
      refTail,
    );
  }
  if (!input.isOperational) {
    return packAgencyProfileDescription(
      `Official profile for ${name}, a former ${agencyTypeLabel.toLowerCase()}${locPhrase ? ` in ${locPhrase}` : ""}. Training history, contacts, and successor information when available`,
      refTail,
    );
  }
  return packAgencyProfileDescription(
    `Official ${agencyTypeLabel}: ${name}${locPhrase ? ` — ${locPhrase}` : ""}. Training events, contacts, and public agency information`,
    refTail,
  );
}

function shortSlugTailForTitle(slug: string): string {
  const s = slug.trim();
  return s.length <= 22 ? s : `${s.slice(0, 19)}…`;
}

/**
 * Parenthetical content: geo (may shorten), then ORI, then numeric slug — tail segments preserved
 * so truncation does not drop #id / ORI first.
 */
function buildAgencyTitleParen(
  locRaw: string,
  oriRaw: string,
  slugNum: string | undefined,
  includeSlugNum: boolean,
  slugFallback: string | undefined,
  maxParenChars: number,
): string {
  const loc = locRaw.replace(/\s+/g, " ").trim();
  const ori = oriRaw.replace(/\s+/g, " ").trim();
  const slugPart =
    includeSlugNum && slugNum
      ? `#${slugNum}`
      : !ori && !loc && slugFallback
        ? shortSlugTailForTitle(slugFallback)
        : "";

  const tail = [ori, slugPart].filter(Boolean).join(" · ");
  if (!loc && !tail) return "";

  if (!tail) {
    return truncateLocationHintPreservingState(loc, maxParenChars);
  }
  if (!loc) {
    return tail.length <= maxParenChars ? tail : truncateSeoTitle(tail, maxParenChars);
  }

  const glue = " · ";
  const tailLen = tail.length + glue.length;
  let locBudget = Math.max(4, maxParenChars - tailLen);
  for (; locBudget >= 3; locBudget--) {
    const locTrunc = truncateLocationHintPreservingState(
      loc,
      Math.min(loc.length, locBudget),
    );
    const joined = `${locTrunc}${glue}${tail}`;
    if (joined.length <= maxParenChars) return joined;
  }
  const oriShort =
    ori.length > 18 ? `${ori.slice(0, 8)}…${ori.slice(-7)}` : ori;
  const tailShort = [oriShort, slugPart].filter(Boolean).join(" · ");
  const tailLen2 = tailShort.length + glue.length;
  const lb2 = Math.max(3, maxParenChars - tailLen2);
  const loc2 = truncateLocationHintPreservingState(loc, Math.min(loc.length, lb2));
  const joined2 = `${loc2}${glue}${tailShort}`;
  if (joined2.length <= maxParenChars) return joined2;
  return truncateSeoTitle(joined2, maxParenChars);
}

function packAgencyTitleFromParts(
  nameRaw: string,
  loc: string,
  ori: string,
  slugNum: string | undefined,
  includeSlugNum: boolean,
  slugStr: string,
  maxChars: number,
): string {
  const name = expandAgencyNameForSeoTitle(nameRaw) || "Agency";
  const locRequired = loc.replace(/\s+/g, " ").trim();
  const slugFallback =
    !ori && !locRequired && !slugNum && slugStr.trim() ? slugStr : undefined;

  if (locRequired) {
    const st = minimalLocationForAgencyTitle(locRequired);
    for (const brand of [SEO_TITLE_BRAND_FULL, SEO_TITLE_BRAND_SHORT]) {
      for (const role of [AGENCY_ROLE_SUFFIX, ""]) {
        const out = `${name} (${st})${role}${brand}`;
        if (out.length <= maxChars) return out;
      }
    }
  }

  for (const brand of [SEO_TITLE_BRAND_FULL, SEO_TITLE_BRAND_SHORT]) {
    for (let maxParen = 72; maxParen >= 6; maxParen -= 2) {
      const paren = buildAgencyTitleParen(
        locRequired,
        ori,
        slugNum,
        includeSlugNum,
        slugFallback,
        maxParen,
      );
      const locParen =
        paren ||
        (locRequired ? minimalLocationForAgencyTitle(locRequired) : "");
      const tail = locParen
        ? ` (${locParen})${AGENCY_ROLE_SUFFIX}${brand}`
        : `${AGENCY_ROLE_SUFFIX}${brand}`;
      const nameBudget = maxChars - tail.length;
      if (nameBudget < 8) continue;
      const nameTrunc = truncateSeoTitle(name, nameBudget);
      const out = `${nameTrunc}${tail}`;
      if (out.length <= maxChars) return out;
    }
  }

  const paren = buildAgencyTitleParen(
    locRequired,
    ori,
    slugNum,
    includeSlugNum,
    slugFallback,
    32,
  );
  const locParen =
    paren || (locRequired ? minimalLocationForAgencyTitle(locRequired) : "");
  const core = locParen
    ? `${name} (${locParen})${AGENCY_ROLE_SUFFIX}`
    : `${name}${AGENCY_ROLE_SUFFIX}`;
  const suffix = SEO_TITLE_BRAND_SHORT;
  const budget = maxChars - suffix.length;
  const truncatedCore = truncateSeoTitle(core, Math.max(budget, 24));
  const out = `${truncatedCore}${suffix}`;
  if (out.length <= maxChars) return out;
  const hardCore = truncateSeoTitle(truncatedCore, Math.max(maxChars - suffix.length, 8));
  return `${hardCore}${suffix}`;
}

/**
 * Title for public agency profile URLs — matches server HTML injection and client SEOHead.
 * Optional `locationHint` (e.g. "County, City, ST") disambiguates duplicate agency names in SERPs.
 * Optional `disambiguator` (e.g. primary ORI) adds a second line inside parentheses when space allows.
 */
export function buildAgencyProfilePageTitle(
  agencyName: string,
  maxChars: number = AGENCY_PROFILE_TITLE_MAX_CHARS,
  locationHint?: string,
  disambiguator?: string,
  slug?: string | null,
): string {
  const loc = (locationHint || "").replace(/\s+/g, " ").trim();
  const ori = (disambiguator || "").replace(/\s+/g, " ").trim();
  const slugNum = extractAgencySlugNumericPrefix(slug);
  const slugStr = (slug ?? "").trim();

  const hasOri = Boolean(ori);
  const hasStateSuffix = locationHintHasUsStateSuffix(loc);
  const includeSlugNum =
    Boolean(slugNum) &&
    !hasOri &&
    (!loc || !hasStateSuffix);

  return packAgencyTitleFromParts(
    agencyName,
    loc,
    ori,
    slugNum,
    includeSlugNum,
    slugStr,
    maxChars,
  );
}

export type EventDetailPageTitleDisambiguators = {
  venueCity?: string | null;
  venueState?: string | null;
  dateLabel?: string | null;
  /** Include only when a sibling offering shares title + city + start date. */
  venueName?: string | null;
};

/** Compact SERP date, e.g. "Jun 12, 2026" or "Jun 12–14, 2026". UTC so SSR and CSR match. */
export function formatEventSeoDateLabel(
  start?: Date | string | null,
  end?: Date | string | null,
): string {
  const toDate = (v: Date | string | null | undefined): Date | null => {
    if (v == null || v === "") return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const utcDayKey = (d: Date) =>
    `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  const s = toDate(start);
  const e = toDate(end);
  if (!s && !e) return "";
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  };
  if (s && e && utcDayKey(s) !== utcDayKey(e)) {
    return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}`;
  }
  const one = s ?? e;
  return one ? one.toLocaleDateString("en-US", opts) : "";
}

export function formatEventSeoLocationLine(
  city?: string | null,
  state?: string | null,
): string {
  return [city, state]
    .map((p) => (p ?? "").toString().replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(", ");
}

export type FormatEventSeoCostLabelOptions = {
  /** When DB cost fields are unset, titles with "free" may still emit FREE in meta. */
  trainingTitle?: string | null;
};

/** Compact cost for auto titles/metas. Empty when unknown. Explicit or title-advertised free is "FREE". */
export function formatEventSeoCostLabel(
  min: unknown,
  max: unknown,
  options?: FormatEventSeoCostLabelOptions,
): string {
  const display = resolveEventPriceDisplay({
    min,
    max,
    title: options?.trainingTitle,
  });
  if (display.kind === "unknown") return "";
  if (display.kind === "free") return "FREE";
  if (display.kind === "range" && display.low != null && display.high != null) {
    return `$${display.low.toFixed(0)} – ${display.high.toFixed(0)}`;
  }
  if (display.amount == null) return "";
  return `$${display.amount.toFixed(0)}`;
}

function packEventDetailTitleFromParts(
  trainingTitle: string,
  loc: string,
  dateLabel: string,
  venueName: string,
  maxChars: number,
): string {
  const title = trainingTitle.replace(/\s+/g, " ").trim() || "Training Event";
  const extras = [loc, dateLabel, venueName].filter(Boolean);

  for (const brand of [SEO_TITLE_BRAND_FULL, SEO_TITLE_BRAND_SHORT]) {
    if (titleIncludesSeoBrand(title) && extras.length === 0) {
      return truncateSeoTitle(title, maxChars);
    }
    const extraStr = extras.length ? ` · ${extras.join(" · ")}` : "";
    const tail = titleIncludesSeoBrand(title) ? extraStr : `${extraStr}${brand}`;
    const nameBudget = maxChars - tail.length;
    if (nameBudget < 8) continue;
    const nameTrunc = truncateSeoTitle(title, nameBudget);
    const out = `${nameTrunc}${tail}`.replace(/\s+/g, " ").trim();
    if (out.length <= maxChars) return out;
  }

  const extraCore = extras.length ? ` · ${extras.join(" · ")}` : "";
  const suffix = SEO_TITLE_BRAND_SHORT;
  const budget = maxChars - suffix.length;
  const core = truncateSeoTitle(`${title}${extraCore}`, Math.max(budget, 24));
  const out = titleIncludesSeoBrand(core) ? core : `${core}${suffix}`;
  if (out.length <= maxChars) return out;
  return truncateSeoTitle(out, maxChars);
}

/**
 * `<title>` for public event detail URLs — keeps Semrush "title too long" in check while
 * matching client `SEOHead` and server HTML injection.
 * Auto titles pack city, date, and optional sibling venue so recurring courses do not collide.
 * `metaTitle` stays verbatim (trimmed + truncated).
 */
export function buildEventDetailPageTitle(
  trainingTitle: string,
  metaTitle?: string | null,
  maxChars: number = EVENT_DETAIL_TITLE_MAX_CHARS,
  disambiguators?: EventDetailPageTitleDisambiguators,
): string {
  const custom = (metaTitle ?? "").replace(/\s+/g, " ").trim();
  if (custom) {
    return truncateSeoTitle(custom, maxChars);
  }
  const loc = formatEventSeoLocationLine(
    disambiguators?.venueCity,
    disambiguators?.venueState,
  );
  const dateLabel = (disambiguators?.dateLabel ?? "").replace(/\s+/g, " ").trim();
  const venueName = (disambiguators?.venueName ?? "").replace(/\s+/g, " ").trim();
  return packEventDetailTitleFromParts(
    trainingTitle,
    loc,
    dateLabel,
    venueName,
    maxChars,
  );
}
